import {
    Box, IconButton, MenuItem, TextField, Typography, styled,
} from '@mui/material';
import { Remove } from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ChipsInput } from '../ChipsInput';

type SetDefinition = Models.SetsPlayground.SetDefinition;
type SetFilterRequest = Models.SetsPlayground.SetFilterRequest;
type ContentMap = NonNullable<SetFilterRequest['content']>;
type ContentPropertyType = Models.SetsPlayground.ContentPropertyType;

type Row = { id: string; key: ContentPropertyType | string; values: string[] };

export interface ContentFilterWidgetProps {
    index?: number;
    set?: SetDefinition;
    filter?: SetFilterRequest;
    onChangeContent: (index: number | undefined, content: ContentMap) => void;
}

const RowGrid = styled('div')(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: '1fr 2fr auto',
    gap: theme.spacing(2),
    alignItems: 'start',
    marginBottom: theme.spacing(2),
}));

const OPTIONS: Array<{ label: string; value: ContentPropertyType }> = [
    { value: 'DesignId', label: 'Design ID' },
    { value: 'KeywordSearchPhrase', label: 'Keyword Search Phrase' },
    { value: 'Keyword', label: 'Exact Keyword' },
    { value: 'DesignConceptId', label: 'Design Concept ID' },
    { value: 'DesignColor', label: 'Design Color' },
    { value: 'ImagePlaceholder', label: 'Image Placeholder' },
    { value: 'NumberOfPhotos', label: 'Number of Photos' },
    { value: 'MinCreatedDate', label: 'Min Created Date' },
    { value: 'MaxCreatedDate', label: 'Max Created Date' },
];

const isDateKey = (k: string | undefined | null): k is 'MinCreatedDate' | 'MaxCreatedDate' => k === 'MinCreatedDate' || k === 'MaxCreatedDate';

const isSingleValueKey = (k: string | undefined | null): boolean => isDateKey(k) || k === 'KeywordSearchPhrase';

const uid = (): string => uuidv4();

const rowsFromContent = (content?: ContentMap): Row[] => {
    const rows: Row[] = [];
    const map = content ?? {};

    (Object.keys(map) as Array<ContentPropertyType>).forEach((k) => {
        const vals = map[k] ?? [];

        rows.push({ id: uid(), key: k, values: vals });
    });
    return rows;
};

const rowsToContent = (rows: Row[]): ContentMap => {
    const acc: Partial<Record<ContentPropertyType, string[]>> = {};

    rows.forEach((r) => {
        const key = (r.key || '').trim() as ContentPropertyType;

        if (!key) {
            return;
        }
        const cleaned = r.values.map((v) => v.trim()).filter((v) => v.length > 0);

        if (cleaned.length === 0) {
            return;
        }
        acc[key] = cleaned;
    });
    return acc as ContentMap;
};

const isCompleteRow = (r: Row): boolean => {
    if (!r.key) {
        return false;
    }
    if (isSingleValueKey(r.key)) {
        return (r.values[0]?.trim().length ?? 0) > 0;
    }
    return r.values.length > 0;
};

const isEmptyRow = (r: Row): boolean => (!r.key || r.key.trim() === '') && (r.values?.length ?? 0) === 0;

const addEmptyRowIfNeeded = (list: Row[]): Row[] => {
    if (!list.length) {
        return [{ id: uid(), key: '', values: [] }];
    }

    const last = list[list.length - 1];
    const hasEmptyRow = isEmptyRow(last);
    const allComplete = list.every(isCompleteRow);
    const hasIncompleteBeforeLast = list.slice(0, -1).some((r) => !isCompleteRow(r));

    if (hasEmptyRow && hasIncompleteBeforeLast) {
        return list.slice(0, -1);
    }

    if (!hasEmptyRow && allComplete) {
        return [...list, { id: uid(), key: '', values: [] }];
    }
    return list;
};

export const ContentFilterWidget = ({
    index,
    set,
    filter,
    onChangeContent,
}: ContentFilterWidgetProps): JSX.Element => {
    const filterToUse = filter ?? set?.filters;
    const [rows, setRows] = useState<Row[]>(
        () => addEmptyRowIfNeeded(rowsFromContent(filterToUse?.content)),
    );

    const usedKeys = new Set(rows.flatMap((r) => (r.key ? [r.key as string] : [])));

    useEffect(() => {
        const incomingContent = filterToUse?.content;
        const currentContent = rowsToContent(rows);

        if (JSON.stringify(incomingContent ?? {}) !== JSON.stringify(currentContent)) {
            setRows(addEmptyRowIfNeeded(rowsFromContent(incomingContent)));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterToUse?.content]);

    const applyContentChanges = (nextRows: Row[]): void => {
        const normalized = addEmptyRowIfNeeded(nextRows);

        setRows(normalized);
        onChangeContent(index, rowsToContent(normalized));
    };

    const updateRowKey = (id: string, key: ContentPropertyType | ''): void => {
        const next = rows.map((r) => (r.id === id ? { ...r, key } : r));

        applyContentChanges(next);
    };

    const updateRowValues = (id: string, values: string[]): void => {
        const next = rows.map((r) => (r.id === id ? { ...r, values } : r));

        applyContentChanges(next);
    };

    const updateRowSingleValue = (id: string, value: string): void => {
        const next = rows.map((r) => (r.id === id ? { ...r, values: value ? [value] : [] } : r));

        applyContentChanges(next);
    };

    const removeRow = (id: string): void => {
        const next = rows.filter((r) => r.id !== id);

        applyContentChanges(next.length ? next : [{ id: uid(), key: '', values: [] }]);
    };

    return (
        <Box mt={2}>
            <Typography gutterBottom fontWeight="medium" sx={{ mb: 3 }} variant="body2">
                Content Filters
            </Typography>
            {rows.map((r, idx) => {
                const dateField = isDateKey(r.key);

                return (
                    <RowGrid key={r.id}>
                        <TextField
                            select
                            label="Content Property"
                            size="small"
                            value={r.key}
                            onChange={(e): void => updateRowKey(r.id, e.target.value as ContentPropertyType | '')}
                        >
                            {OPTIONS.map((opt) => (
                                <MenuItem
                                    disabled={r.key !== opt.value && usedKeys.has(opt.value)}
                                    key={opt.value}
                                    value={opt.value}
                                >
                                    {opt.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        {isSingleValueKey(r.key) ? (
                            <TextField
                                label={OPTIONS.find((opt) => opt.value === r.key)?.label}
                                placeholder={dateField ? 'YYYY-MM-DD' : undefined}
                                size="small"
                                value={r.values[0] ?? ''}
                                onChange={(e): void => updateRowSingleValue(r.id, e.target.value)}
                            />
                        ) : (
                            <ChipsInput
                                label="Values"
                                values={r.values}
                                onChange={(vals): void => updateRowValues(r.id, vals)}
                            />
                        )}
                        <IconButton
                            aria-label="remove row"
                            disabled={rows.length <= 1 && idx === rows.length - 1}
                            size="small"
                            onClick={(): void => removeRow(r.id)}
                        >
                            <Remove />
                        </IconButton>
                    </RowGrid>
                );
            })}
        </Box>
    );
};
